import React from 'react';
import { useTranslation } from 'react-i18next';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { Popper } from '@mui/material';
import { ArcDialog, ArcIconButton, ArcPagination, ArcStack, ArcText } from '@src/core/ui';
import { NotificationEntity, NotificationsModalProps } from '../types';
import { useHoverMenu } from '../useHoverMenu';
import NotificationItem from '../notification-item';
import MoreMenuPanel from '../more-menu-panel';

const PAGE_SIZE = 10;

const NotificationsModal: React.FC<NotificationsModalProps> = ({ open, notifications, onClose, onMarkAllAsRead, onItemClick }) => {
  const { t } = useTranslation();
  const more = useHoverMenu();
  const [page, setPage] = React.useState(1);

  React.useEffect(() => {
    if (open) setPage(1);
  }, [open]);

  const pageCount = Math.max(1, Math.ceil(notifications.length / PAGE_SIZE));
  const pagedNotifications = notifications.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  React.useEffect(() => {
    if (page > pageCount) setPage(pageCount);
  }, [page, pageCount]);

  const handleClose = () => {
    more.close();
    onClose();
  };

  const handleItemClick = (notification: NotificationEntity) => {
    if (onItemClick(notification)) handleClose();
  };

  const handleMarkAllAsRead = () => {
    onMarkAllAsRead();
    more.close();
  };

  return (
    <>
      <ArcDialog
        open={open}
        onClose={handleClose}
        title={<ArcText.h5 fontWeight={600}>{t('notifications.title')}</ArcText.h5>}
        titleDivider
        headerIconSize={24}
        contextSX={{
          minWidth: 480,
          px: 3,
          pt: 0,
          pb: 2,
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          overflowY: 'hidden',
        }}
        PaperProps={{
          sx: {
            height: '87vh',
            maxHeight: '95vh',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          },
        }}
        headerActions={
          <ArcStack onMouseEnter={more.openMenu} onMouseLeave={more.scheduleClose}>
            <ArcIconButton size='small' sx={{ p: 0 }} aria-haspopup='menu' aria-expanded={more.open}>
              <MoreVertIcon sx={{ fontSize: 24, color: more.open ? 'brand.main' : 'text.primary' }} />
            </ArcIconButton>
          </ArcStack>
        }>
        <ArcStack spacing={2} sx={{ flex: 1, minHeight: 0, overflow: 'hidden', justifyContent: 'space-between' }}>
          <ArcStack sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
            <div style={{ height: '100%', overflowY: 'auto' }}>
              {notifications.length === 0 ? (
                <ArcStack alignItems='center' sx={{ py: 4 }}>
                  <ArcText.h5 fontSize={12} color='text.secondary'>
                    {t('notifications.empty')}
                  </ArcText.h5>
                </ArcStack>
              ) : (
                pagedNotifications.map((notification) => (
                  <NotificationItem key={notification.id} notification={notification} onClick={handleItemClick} rowSx={{ px: 0 }} />
                ))
              )}
            </div>
          </ArcStack>
          {notifications.length > PAGE_SIZE ? (
            <ArcStack alignItems='center' flexShrink={0}>
              <ArcPagination
                count={pageCount}
                page={page}
                onChange={(_event, value) => setPage(value)} />
            </ArcStack>
          ) : null}
        </ArcStack>
      </ArcDialog>

      <Popper
        open={open && more.open && Boolean(more.anchorEl)}
        anchorEl={more.anchorEl}
        placement='bottom-end'
        sx={{ zIndex: (theme) => theme.zIndex.modal + 1 }}
        onMouseEnter={() => more.openMenu()}
        onMouseLeave={more.scheduleClose}>
        <ArcStack sx={{ pt: '6px' }}>
          <MoreMenuPanel
            onMarkAllAsRead={handleMarkAllAsRead}
            onNotificationSettings={handleClose} />
        </ArcStack>
      </Popper>
    </>
  );
};

export default NotificationsModal;
